import PropTypes from 'prop-types';
import { overPerBuyerLimit } from '../utils/overPerBuyerLimit';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import Tippy from '@tippyjs/react';

import VerticalInsure from './VerticalInsure';
import { toast } from 'react-toastify';

import TurnstileChallenge from '../../../shared/TurnstileChallenge';
import pickTotalsTable from '../utils/pickTotalsTable';
import buildCompleteUrl from '../utils/buildCompleteUrl';
import PoweredByLogo from './PoweredByLogo';

import CustomFieldValueFields from './../../CustomFieldValues/components/CustomFieldValueFields';
import {canSubmitCustomFieldValue} from './../../CustomFieldValues/actions/customFieldValuesActionCreators';
import { checkoutAnswers } from '../../CustomFieldValues/perTicketAnswers';
import TicketAnswerBlocks from '../../CustomFieldValues/components/TicketAnswerBlocks';
import { validatePostalCode, detectCountryFromPostalCode, getPostalCodeMask } from '../../../shared/postalCodeUtils';

import MaskedInput from 'react-text-mask'
import { Checkbox } from "@opendate-app/setlist";
import {useStripe, useElements} from '@stripe/react-stripe-js';
import CheckoutPaymentSection from './CheckoutPaymentSection';
import GiftCardRedemptionField from './GiftCardRedemptionField';
import CheckoutWaitPanel from './CheckoutWaitPanel';
import useElapsedSeconds from '../../../shared/useElapsedSeconds';
var _ = require('lodash');
const axios = require("axios").default;

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
function validateEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

function validateZip(zip) {
  return validatePostalCode(zip);
}

const completeOrderWaitStage = (elapsed) => {
  if (elapsed < 5) {
    return { title: "Processing your order..." };
  } else if (elapsed < 15) {
    return { title: "Still working on it \u2014 this may take a moment..." };
  } else {
    return { lead: "Hang tight!", title: "Your order is being processed. Please don\u2019t close this window." };
  }
};

const turnstileDiagnosticError = (error) => ({
  error_name: error && error.name,
  error_message: error && error.message,
  turnstile_error_code: error && error.turnstileDetails && error.turnstileDetails.code,
  turnstile_details: (error && error.turnstileDetails) || {}
});

const checkIfEmailAtTicketLimit = (csrfToken, confirm, ticketReservation, ticketTypes, setMaxTicketsPerEmailReached) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

  var hasValidEmail = (
    ticketReservation.email
      && ticketReservation.email.length > 0
      && validateEmail(ticketReservation.email)
  );

  if(!hasValidEmail){
    setMaxTicketsPerEmailReached(false);
    return;
  }

  axios.get(`/confirms/${confirm.id}/web_orders/lookup_ticket_count_by_email`, {
    params: {
      email: ticketReservation.email
    }
  })
  .then(({data}) => {
    var owned = data;
    var inCart = ticketTypes.reduce((sum, tt) => {
      return sum + parseInt(tt.quantity || 0);
    }, 0);

    setMaxTicketsPerEmailReached(
      overPerBuyerLimit(owned + inCart, confirm.max_tickets_per_email)
    )
  });
};
const debouncedCheckIfEmailAtTicketLimit = _.debounce(checkIfEmailAtTicketLimit, 500);

export const emailsMatch = (email, confirmEmail) => (
  (email || "").trim().toLowerCase() === (confirmEmail || "").trim().toLowerCase()
);

const checkIfConfirmEmailMatches = (ticketReservation, ticketReservationChanged) => {
  var confirmEmailDoesNotMatch = (
    ticketReservation
      && ticketReservation.email
      && ticketReservation.email.length > 0
      && ticketReservation.confirm_email
      && ticketReservation.confirm_email.length > 0
      && !emailsMatch(ticketReservation.email, ticketReservation.confirm_email)
  );

  var updated = Object.assign({}, ticketReservation, {confirmEmailDoesNotMatch: confirmEmailDoesNotMatch});
  ticketReservationChanged(updated);
};
const debouncedCheckIfConfirmEmailMatches = _.debounce(checkIfConfirmEmailMatches, 500);
var debouncedAutoSaveTicketReservation;

const handleVerticalInsureEvent = (
  event,
  insuranceOfferState,
  insuranceOfferStateChanged,
  ticketInsurance,
  csrfToken,
  confirm,
  ticketReservation,
  registerTicketInsurance,
  deleteTicketInsurance
) => {
  var selectedState = event.detail.selectedState;

  if(selectedState === "accepted"){
    registerTicketInsurance(csrfToken, confirm, ticketReservation, event.detail.quote);
  } else {
    deleteTicketInsurance(csrfToken, confirm, ticketInsurance);
  }

  insuranceOfferStateChanged(selectedState);
};
const debouncedHandleVerticalInsureEvent = _.debounce(handleVerticalInsureEvent, 300);

function geoCodeContactZip(zipCode, saveGeoCodedStateCallback, defaultCountry) {
  var geocoder = new google.maps.Geocoder();
  var country = detectCountryFromPostalCode(zipCode) || defaultCountry || 'US';

  var geocodingRequest = {
    address: zipCode,
    componentRestrictions: {
      country: country
    }
  };

  geocoder.geocode(geocodingRequest, function(results, status) {
    if (status === google.maps.GeocoderStatus.OK) {
      if (results.length > 0) {
        var addressComponents = results[0].address_components;
        var component = addressComponents.find((ac) => {
          return ac.types.includes("administrative_area_level_1");
        });

        if(component){
          saveGeoCodedStateCallback(component.short_name);
        } else {
          saveGeoCodedStateCallback(null);
        }
      } else {
        saveGeoCodedStateCallback(null);
      }
    } else {
      saveGeoCodedStateCallback(null);
    }
  });
}

const setContactStateCode = (csrfToken, confirm, ticketReservation, saveGeoCodedStateCode, contactZip, defaultCountry) => {
  var validZip = (
    contactZip
      && contactZip.length > 0
      && validateZip(contactZip)
  );

  var callback = (stateCode) => {
    saveGeoCodedStateCode(csrfToken, confirm, ticketReservation, stateCode);
  };

  if(validZip){
    geoCodeContactZip(contactZip, callback, defaultCountry);
  } else {
    callback(null);
  }
};
const debouncedSetContactStateCode = _.debounce(setContactStateCode, 250);

const CheckoutForm = ({
  team,
  useDefaultTheme,
  widgetTemplateId,
  widgetType,
  csrfToken,
  confirm,
  ticketReservation,
  deleteTicketReservation,
  stripePromise,
  ticketReservationChanged,
  stripeError,
  stripeErrorChanged,
  updateTicketReservation,
  rootUrl,
  stripePaymentRequired,
  ticketTypes,
  promoCode,
  isPlacingOrder,
  isPlacingOrderChanged,
  addOns,
  customFieldValues: allCustomFieldValues,
  customFieldValueChanged,
  postToParent,
  seatsIOSelectedObjects,
  autoSaveTicketReservation,
  userContext,
  saveGeoCodedStateCode,
  verticalInsureClientID,
  registerTicketInsurance,
  insuranceOfferState,
  insuranceOfferStateChanged,
  ticketInsurance,
  deleteTicketInsurance,
  hasInsuranceQuote,
  hasInsuranceQuoteChanged,
  defaultCountry,
  selectedMembershipProduct,
  resaleListings,
  resaleOrderItems
}) => {
  const { orderAnswers, blocks: ticketBlocks, all: customFieldValues } = checkoutAnswers(allCustomFieldValues || [], ticketTypes);
  const formRef = useRef(null);
  const zipCodeRef = useRef(null);
  const turnstileRef = useRef(null);
  const verifyingHumanRef = useRef(false);
  const [verifyingHuman, setVerifyingHuman] = useState(false);
  const stripeErrorRef = useRef(null);
  const [maxTicketsPerEmailReached, setMaxTicketsPerEmailReached] = useState(false);
  const [completingOrder, setCompletingOrder] = useState(null);
  const completingElapsed = useElapsedSeconds(Boolean(completingOrder));

  const [giftcardCode, setGiftcardCode] = useState("");
  const [giftcardError, setGiftcardError] = useState(null);

  // A gift card cannot be used on an order that includes a donation, so hide the entry
  // entirely when one is present (the server rejects it as a backstop).
  const hasDonation = ticketReservation.abstract_order_items?.some(
    item => item.custom_charge_type_type === "CustomChargeTypes::Donation"
  );

  const reportTurnstileFailure = (error) => {
    const diagnostic = {
      ...turnstileDiagnosticError(error),
      confirm_id: confirm.id,
      ticket_reservation_id: ticketReservation.id,
      hostname: window.location.hostname,
      href: window.location.href,
      site_key_present: Boolean(typeof gon !== "undefined" && gon.turnstile_site_key),
      turnstile_present: Boolean(window.turnstile),
      turnstile_script_present: Boolean(document.getElementById("cloudflare-turnstile-js"))
    };

    console.error("[CheckoutTurnstileFailure]", diagnostic, error);

    if (window.DD_RUM && typeof window.DD_RUM.addError === "function") {
      window.DD_RUM.addError(error, diagnostic);
    }

    axios
      .post(`/confirms/${confirm.id}/web_orders/turnstile_failure`, diagnostic)
      .catch((error) => console.warn("[CheckoutTurnstileFailure] diagnostic report failed", error));
  };

  const lookupGiftcard = (ticketReservation, ticketReservationChanged) => {
    if(giftcardError) {
      setGiftcardError(null);
    }

    if(giftcardCode.length === 0){
      return;
    }

    axios.get(`/confirms/${confirm.id}/web_orders/lookup_giftcard`, {
      params: {
        code: giftcardCode
      }
    })
      .then(({data}) => {
        if(data && data.active) {

          ticketReservationChanged({
            ...ticketReservation,
            giftcard: data
          });
        } else {
          setGiftcardError("Invalid giftcard code");
        }
      })
      .catch((error) => {
        setGiftcardError("Invalid giftcard code");
      });
  }

  const applyGiftcard = (csrfToken, confirm, ticketReservation) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    var ticketReservationParams;

    if(ticketReservation.gift_up_redemption?._destroy){
      ticketReservationParams = {
        gift_up_redemption_attributes: {
          id: ticketReservation.gift_up_redemption.id,
          _destroy: true
        }
      }
    } else {
      ticketReservationParams = {
        gift_up_redemption_attributes: {
          redeemed_amount: 0,
          gift_up_giftcard_id: ticketReservation.giftcard.id,
        }
      }
    }

    return axios.patch("/confirms/" + confirm.id + "/ticket_reservations/" + ticketReservation.token, ticketReservationParams)
      .then(({ data }) => {
        var updated = {
          ...ticketReservation,
          ...data
        };

        if(ticketReservation.gift_up_redemption?._destroy){
          delete updated.gift_up_redemption;
        }

        ticketReservationChanged(updated);
        deleteTicketInsurance(csrfToken, confirm, ticketInsurance);
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const TotalsComponent = pickTotalsTable(widgetType, resaleOrderItems);

  var stripe;
  var elements;

  if(stripePaymentRequired(ticketReservation)){
    stripe = useStripe();
    elements = useElements();
  }

  const [venueAgreements, setVenueAgreements] = useState(confirm.venue_agreements.map((agreement) => {
    return {
      ...agreement,
      accepted: false
    }
  }));

  const [resaleTermsAccepted, setResaleTermsAccepted] = useState(false);

  const requiredAgreementsAccepted = venueAgreements.every(
    (agreement) => agreement.required === false || agreement.accepted
  ) && (!ticketReservation.resale_buyer_terms || resaleTermsAccepted);

  const acceptedVenueAgreementIds = () => (
    venueAgreements.filter((agreement) => agreement.accepted).map((agreement) => agreement.id)
  );

  const zipCodeIsValid = (ticketReservation) => {
    return (
      ticketReservation.contact_zip
        && ticketReservation.contact_zip.length > 0
        && validateZip(ticketReservation.contact_zip)
    );
  }

  const canCheckout = (ticketReservation, isPlacingOrder, maxTicketsPerEmailReached) => {
    var stripeRequired = stripePaymentRequired(ticketReservation);

    return (
      (!stripeRequired || (stripeRequired && stripe && elements))
        && Object.keys(ticketReservation).length > 0
        && ticketReservation.token
        && ticketReservation.token.length > 0
        && ticketReservation.first_name
        && ticketReservation.first_name.length > 0
        && ticketReservation.last_name
        && ticketReservation.last_name.length > 0
        && ticketReservation.email
        && ticketReservation.email.length > 0
        && validateEmail(ticketReservation.email)
        && emailsMatch(ticketReservation.email, ticketReservation.confirm_email)
        && !isPlacingOrder
        && customFieldValues.every((cfv) => canSubmitCustomFieldValue(cfv, { requireCheckedCheckbox: true }))
        && requiredAgreementsAccepted
        && !maxTicketsPerEmailReached
    );
  }

  const completeOrder = (completeUrl, paid) => {
    postToParent("opendateOrderPlaced", {});
    setCompletingOrder({ paid });
    window.location.replace(completeUrl);
  };

  const confirmStripePayment = (returnUrl) => {
    stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl
      },
      redirect: "if_required"
    })
    .then(function(result) {
      if (result.error) {
        isPlacingOrderChanged(false);
        stripeErrorChanged(result.error);
      } else {
        completeOrder(returnUrl, true);
      }
    })
    .catch(function(error) {
      if (window.Sentry) window.Sentry.captureException(error);

      isPlacingOrderChanged(false);
      stripeErrorChanged({
        message: "Something went wrong processing your payment. Please try again."
      });
    });
  };

  const handleSubmit = async (event) => {
    stripeErrorChanged({});

    if(confirm.checkout_captcha_required){
      verifyingHumanRef.current = true;
      setVerifyingHuman(true);
      try {
        if (!turnstileRef.current) {
          throw new Error("Turnstile widget not available");
        }

        var token = await turnstileRef.current.executeAsync();

        if (!token){
          throw new Error("Turnstile token missing")
        };

        ticketReservation = Object.assign({}, ticketReservation, {checkout_captcha_token: token});
      } catch (error) {
        reportTurnstileFailure(error);

        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }

        stripeErrorChanged({
          message: "Unable to verify you are human. Please try again."
        });

        return;
      } finally {
        verifyingHumanRef.current = false;
        setVerifyingHuman(false);
      }
    }

    isPlacingOrderChanged(true);

    var completeUrl = buildCompleteUrl(rootUrl, confirm.id, ticketReservation.token, useDefaultTheme, widgetTemplateId);

    updateTicketReservation(csrfToken, confirm, ticketReservation, (ticketReservation) => {
      if(stripePaymentRequired(ticketReservation)){
        confirmStripePayment(completeUrl);
      } else {
        completeOrder(completeUrl, false);
      }
    }, customFieldValues, acceptedVenueAgreementIds(), ticketReservation.resale_buyer_terms ? resaleTermsAccepted : undefined);
  };

  const canShowInsurance = (verticalInsureClientID, confirm, ticketReservation) => {
    const INSURANCE_SUPPORTED_COUNTRIES = ['US'];
    
    return (
      verticalInsureClientID
        && verticalInsureClientID.length > 0
        && confirm.ticket_insurance_enabled
        && ticketReservation.contact_zip
        && ticketReservation.contact_zip.length > 0
        && INSURANCE_SUPPORTED_COUNTRIES.includes(detectCountryFromPostalCode(ticketReservation.contact_zip))
        && ticketReservation.contact_state_code
        && ticketReservation.contact_state_code.length > 0
        && ticketReservation.amount_for_vertical_insure
        && ticketReservation.amount_for_vertical_insure > 0
    );
  };

  const memoVerticalInsure = useMemo(() => {
    return (
      <VerticalInsure clientId={verticalInsureClientID}
                      eventStartDate={confirm.iso_start_date}
                      eventEndDate={confirm.iso_end_date}
                      state={ticketReservation.contact_state_code}
                      postalCode={ticketReservation.contact_zip}
                      insurableAmount={ticketReservation.amount_for_vertical_insure}
                      data-unique-id={ticketReservation.id}
                      onChange={
                        (event) => {
                          debouncedHandleVerticalInsureEvent(
                            event,
                            insuranceOfferState,
                            insuranceOfferStateChanged,
                            ticketInsurance,
                            csrfToken,
                            confirm,
                            ticketReservation,
                            registerTicketInsurance,
                            deleteTicketInsurance
                          );
                        }
                      }
                      onQuoteChange={
                        (event) => {
                          hasInsuranceQuoteChanged(true);

                          debouncedHandleVerticalInsureEvent(
                            event,
                            insuranceOfferState,
                            insuranceOfferStateChanged,
                            ticketInsurance,
                            csrfToken,
                            confirm,
                            ticketReservation,
                            registerTicketInsurance,
                            deleteTicketInsurance
                          );
                        }
                      }
                      onOfferStateChange={
                        (event) => {
                          debouncedHandleVerticalInsureEvent(
                            event,
                            insuranceOfferState,
                            insuranceOfferStateChanged,
                            ticketInsurance,
                            csrfToken,
                            confirm,
                            ticketReservation,
                            registerTicketInsurance,
                            deleteTicketInsurance
                          );
                        }
                      }
                      participantType="SPECTATOR" />
    );
  }, [
    verticalInsureClientID,
    ticketReservation,
    confirm,
    insuranceOfferState,
    insuranceOfferStateChanged,
    ticketInsurance,
    csrfToken,
    confirm,
    registerTicketInsurance,
    deleteTicketInsurance
  ]);

  const [highlightInvalidInputs, setHighlightInvalidInputs] = useState(false);

  useEffect(() => {
    debouncedAutoSaveTicketReservation = _.debounce((csrfToken, confirm, ticketReservation) => {
      autoSaveTicketReservation(csrfToken, confirm, ticketReservation);
    }, 250);
  }, []);

  useEffect(() => {
    if(Object.keys(userContext).length > 0){
      debouncedCheckIfEmailAtTicketLimit(csrfToken, confirm, ticketReservation, ticketTypes, setMaxTicketsPerEmailReached);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(stripeError).length > 0 && stripeErrorRef.current) {
      stripeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [stripeError]);

  return (
    <div className="checkout-wait-host">
    {completingOrder ? (
      <CheckoutWaitPanel {...completeOrderWaitStage(completingElapsed)}
                         detail={completingOrder.paid ? "Your payment has been received and your tickets are being prepared." : "Your tickets are being prepared."} />
    ) : null}
    <form ref={formRef} onSubmit={
            (e) => {
              e.preventDefault();

              if (isPlacingOrder) return false;
              if (verifyingHumanRef.current) return false;

              var element = zipCodeRef.current.inputElement;
              var isZipCodeValid = zipCodeIsValid(ticketReservation);

              if(isZipCodeValid){
                element.classList.remove('highlight');
              } else {
                element.classList.add('highlight');
              }

              if(!canCheckout(ticketReservation, isPlacingOrder, maxTicketsPerEmailReached) || !isZipCodeValid){
                setHighlightInvalidInputs(true);
                formRef.current.scrollIntoView();

                return false;
              }

              handleSubmit(e);
            }
          }>
      <div className="row">
        <div className="col-12">
          <p style={{
               "fontSize": "16px",
               "marginBottom": "12px"
             }}>
            <strong>Contact Info</strong>
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="form-row mb-3">
            <div className="col">
              <input type="text"
                     placeholder="First name"
                     style={{"border": "1px solid #e6e6e6", "fontSize": "14px"}}
                     value={ticketReservation.first_name || ""}
                     disabled={Boolean(userContext.access_token && userContext.first_name)}
                     onChange={
                       (e) => {
                         var updated = Object.assign({}, ticketReservation, {first_name: e.target.value});
                         ticketReservationChanged(updated);
                         debouncedAutoSaveTicketReservation(csrfToken, confirm, updated);
                       }
                     }
                     className="form-control" />
            </div>
            <div className="col">
              <input type="text"
                     placeholder="Last name"
                     style={{"border": "1px solid #e6e6e6", "fontSize": "14px"}}
                     value={ticketReservation.last_name || ""}
                     disabled={Boolean(userContext.access_token && userContext.last_name)}
                     onChange={
                       (e) => {
                         var updated = Object.assign({}, ticketReservation, {last_name: e.target.value});
                         ticketReservationChanged(updated);
                         debouncedAutoSaveTicketReservation(csrfToken, confirm, updated);
                       }
                     }
                     className="form-control" />
            </div>
          </div>
          <div className="form-row">
            <div className="col-6">
              <input type="email"
                     placeholder="Email address"
                     autoCapitalize="off"
                     autoCorrect="off"
                     style={{"border": "1px solid #e6e6e6", "fontSize": "14px"}}
                     value={ticketReservation.email || ""}
                     disabled={userContext.access_token}
                     onChange={
                       (e) => {
                         var updated = Object.assign({}, ticketReservation, {email: e.target.value});

                         ticketReservationChanged(updated);
                         debouncedCheckIfConfirmEmailMatches(updated, ticketReservationChanged);
                         debouncedCheckIfEmailAtTicketLimit(csrfToken, confirm, updated, ticketTypes, setMaxTicketsPerEmailReached);
                         debouncedAutoSaveTicketReservation(csrfToken, confirm, updated);
                       }
                     }
                     className={"form-control " + (ticketReservation.confirmEmailDoesNotMatch || maxTicketsPerEmailReached ? "highlight" : "")} />
            </div>
            <div className="col-6">
              <input type="email"
                     placeholder="Confirm email"
                     autoCapitalize="off"
                     autoCorrect="off"
                     style={{"border": "1px solid #e6e6e6", "fontSize": "14px"}}
                     value={ticketReservation.confirm_email || ""}
                     disabled={userContext.access_token}
                     onChange={
                       (e) => {
                         var updated = Object.assign({}, ticketReservation, {confirm_email: e.target.value});

                         ticketReservationChanged(updated);
                         debouncedCheckIfConfirmEmailMatches(updated, ticketReservationChanged);
                       }
                     }
                     className={"form-control " + (ticketReservation.confirmEmailDoesNotMatch ? "highlight" : "")} />
              {ticketReservation.confirmEmailDoesNotMatch ? (
                <p className="mb-0 mt-1 text-danger small">These emails don't match.</p>
              ) : null}
            </div>
            {maxTicketsPerEmailReached ? (
              <div className="col-12">
                <p className='mb-0 mt-1 text-danger small'>
                  <strong>{`This event has a limit of ${confirm.max_tickets_per_email} tickets per purchaser.`}</strong>
                </p>
              </div>
            ) : null}
          </div>
          <div className="form-row mt-3">
            <div className="col-6">
              <MaskedInput type="text"
                     placeholder="Postal code (required)"
                     guide={false}
                     ref={zipCodeRef}
                     mask={(value) => getPostalCodeMask(value, value ? null : defaultCountry)}
                     pipe={
                       (conformedValue) => conformedValue.toUpperCase()
                     }
                     style={{"border": "1px solid #e6e6e6", "fontSize": "14px"}}
                     value={ticketReservation.contact_zip || ""}
                     onChange={
                       (e) => {
                         var contactZip = e.target.value;
                         var updated = Object.assign({}, ticketReservation, {
                           contact_zip: contactZip,
                           contact_state_code: null
                         });

                         ticketReservationChanged(updated);
                         deleteTicketInsurance(csrfToken, confirm, ticketInsurance);
                         hasInsuranceQuoteChanged(false);

                         if(zipCodeIsValid(updated)){
                           zipCodeRef.current.inputElement.classList.remove('highlight');
                         }

                         autoSaveTicketReservation(csrfToken, confirm, updated).then(
                            () => {
                              debouncedSetContactStateCode(csrfToken, confirm, updated, saveGeoCodedStateCode, contactZip, defaultCountry);
                            }
                         );
                       }
                     }
                     className="form-control" />
              {/* Hidden input for Playwright E2E testing - MaskedInput doesn't work with Playwright's fill() */}
              <input type="text"
                     data-testid="postal-code"
                     tabIndex={-1}
                     aria-hidden="true"
                     style={{position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden', pointerEvents: 'none'}}
                     value={ticketReservation.contact_zip || ""}
                     onChange={
                       (e) => {
                         var contactZip = e.target.value.toUpperCase();
                         var updated = Object.assign({}, ticketReservation, {
                           contact_zip: contactZip,
                           contact_state_code: null
                         });

                         ticketReservationChanged(updated);
                         deleteTicketInsurance(csrfToken, confirm, ticketInsurance);
                         hasInsuranceQuoteChanged(false);

                         if(zipCodeIsValid(updated)){
                           zipCodeRef.current.inputElement.classList.remove('highlight');
                         }

                         autoSaveTicketReservation(csrfToken, confirm, updated).then(
                            () => {
                              debouncedSetContactStateCode(csrfToken, confirm, updated, saveGeoCodedStateCode, contactZip, defaultCountry);
                            }
                         );
                       }
                     } />
            </div>
            <CustomFieldValueFields team={team}
                              csrfToken={csrfToken}
                              customFieldValues={orderAnswers.filter((cfv) => cfv.type === "CustomFieldValues::Phone")}
                              customFieldValueChanged={customFieldValueChanged}
                              confirm={confirm}
                              highlightInvalidInputs={highlightInvalidInputs} />
          </div>
        </div>
      </div>
      <CustomFieldValueFields team={team}
                              csrfToken={csrfToken}
                              customFieldValues={orderAnswers.filter((cfv) => cfv.type !== "CustomFieldValues::Phone")}
                              customFieldValueChanged={customFieldValueChanged}
                              confirm={confirm}
                              highlightInvalidInputs={highlightInvalidInputs}
                              defaultCountry={defaultCountry} />
      <TicketAnswerBlocks blocks={ticketBlocks}
                          team={team}
                          csrfToken={csrfToken}
                          customFieldValueChanged={customFieldValueChanged}
                          confirm={confirm}
                          highlightInvalidInputs={highlightInvalidInputs}
                          defaultCountry={defaultCountry} />
      {canShowInsurance(verticalInsureClientID, confirm, ticketReservation) ? (
        <div className="row">
          <div className={"col-12 " + (hasInsuranceQuote ? "" : "d-none")}
               style={{"marginTop": "23px"}}>
            {memoVerticalInsure}
          </div>
        </div>
      ) : null}
      {stripePaymentRequired(ticketReservation) ? (
        <CheckoutPaymentSection stripeError={stripeError}
                                stripeErrorRef={stripeErrorRef}
                                defaultCountry={defaultCountry} />
      ) : null}
      {confirm.gift_cards_enabled ? (
        <GiftCardRedemptionField csrfToken={csrfToken}
                                 confirm={confirm}
                                 ticketReservation={ticketReservation}
                                 ticketReservationChanged={ticketReservationChanged}
                                 hasDonation={hasDonation}
                                 onGiftCardsChanged={() => deleteTicketInsurance(csrfToken, confirm, ticketInsurance)} />
      ) : null}
      {!confirm.gift_cards_enabled && confirm.venue_ownership.has_giftcards && !hasDonation ? (
        <div className="row"
             style={{"marginTop": "23px"}}>
          <div className="col-12">
            <p className="mb-1"
               style={{"fontSize": "16px"}}>
              <strong>Giftcards</strong>
            </p>
            {ticketReservation.giftcard ? (
              ticketReservation.gift_up_redemption ? (
                <div className="form-row">
                  <div className="col-12">
                    <p className="mb-0">
                      Your giftcard has been applied!{" "}
                      <strong>{formatter.format(ticketReservation.gift_up_redemption.redeemed_amount || 0.00)}</strong> out of{" "}
                      <strong>{formatter.format(ticketReservation.giftcard.remaining_value || 0.00)}</strong> will be redeemed.{" "}
                      <a
                        href="#"
                        onClick={e => {
                          const updated = {
                            ...ticketReservation,
                            gift_up_redemption: {
                              ...ticketReservation.gift_up_redemption,
                              _destroy: true
                            }
                          };

                          applyGiftcard(csrfToken, confirm, updated)
                        }}
                      >
                        Remove
                      </a>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="form-row">
                  <div className="col-12">
                    <p className="mb-0">
                      Your giftcard has a balance of <strong>{formatter.format(ticketReservation.giftcard.remaining_value || 0.00)}</strong>. <a
                        href="#"
                        onClick={e => applyGiftcard(csrfToken, confirm, ticketReservation)}
                      >
                        Apply it now.
                      </a>
                    </p>
                  </div>
                </div>
              )
            ) : (
              <>
                <p>Have a giftcard? Enter the code below to redeem it.</p>
                <div className="form-row">
                  <div className="col-6">
                    <input
                      type="text"
                      placeholder="Giftcard code"
                      className={`form-control ${giftcardError ? "border-danger" : ""}`}
                      style={{"border": "1px solid #e6e6e6", "fontSize": "14px"}}
                      value={giftcardCode || ""}
                      onChange={e => setGiftcardCode(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <button
                      disabled={giftcardCode.length === 0}
                      type="button"
                      className="btn btn-danger px-2 py-1"
                      onClick={e => lookupGiftcard(ticketReservation, ticketReservationChanged)}
                    >
                      Lookup
                    </button>
                  </div>
                  {giftcardError ? (
                    <div className="col-12">
                      <p className="text-danger mt-2 mb-0">
                        {giftcardError}
                      </p>
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
      <div className="row"
           style={{"marginTop": "20px", "marginBottom": "20px"}}>
        <div className="col-12">
          {confirm.agency_event ? (
            <Checkbox
              id="order-subscribe-to-artist"
              checked={ticketReservation.subscribe_to_artist || false}
              onChange={(e) => ticketReservationChanged({...ticketReservation, subscribe_to_artist: e.target.checked})}
              label={`Keep me posted about news from ${confirm.artist_name || confirm.organizer_name}`}
            />
          ) : (
            <Checkbox
              id="order-subscribe-to-venue"
              checked={ticketReservation.subscribe_to_venue || false}
              onChange={(e) => ticketReservationChanged({...ticketReservation, subscribe_to_venue: e.target.checked})}
              label={confirm.venue_ownership.marketing_optin_text
                ? confirm.venue_ownership.marketing_optin_text
                : `Keep me posted about events and news from ${confirm.organizer_name}`}
            />
          )}
        </div>
      </div>
      {venueAgreements.map((venueAgreement) => {
        return (
          <div
            className="row"
            style={{"marginTop": "20px", "marginBottom": "20px"}}
            key={venueAgreement.id}
          >
            <div className="col-12">
              <Checkbox
                id={`order-venue-agreement-${venueAgreement.id}`}
                checked={venueAgreement.accepted}
                onChange={(e) => {
                  setVenueAgreements((current) => current.map((currentAgreement) => (
                    currentAgreement.id === venueAgreement.id ? {...currentAgreement, accepted: e.target.checked} : currentAgreement
                  )));
                }}
                label={<div dangerouslySetInnerHTML={{ __html: venueAgreement.body }} />}
              />
            </div>
          </div>
        )
      })}
      {ticketReservation.resale_buyer_terms ? (
        <div className="row" style={{"marginTop": "20px", "marginBottom": "20px"}}>
          <div className="col-12">
            <Checkbox
              id="order-resale-buyer-terms"
              checked={resaleTermsAccepted}
              onChange={(e) => setResaleTermsAccepted(e.target.checked)}
              label={ticketReservation.resale_buyer_terms}
            />
          </div>
        </div>
      ) : null}
      <div className="row" style={{"marginBottom": "26px"}}>
        <div className="col-xs-auto" style={{"paddingLeft": "15px"}}>
          <i className="fas fa-exclamation-circle small" style={{"color": "#f8cb58"}}></i>
        </div>
        <div className="col">
          <p className="mb-0 small"
             style={{"color": "#686868"}}>
             By clicking "Place Order," I confirm that I have read and accept the <a target="_blank" href="https://www.opendate.io/terms">Terms of Service</a>, <a target="_blank" href="https://www.opendate.io/privacy">Privacy Policy</a> and Refund Policy. I also agree that Opendate may share my information with the event organizer.
          </p>
        </div>
      </div>
      <div className="row d-md-none">
        <div className="col"
             style={{"marginBottom": "15px"}}>
          <TotalsComponent ticketTypes={ticketTypes}
                       promoCode={promoCode}
                       ticketReservation={ticketReservation}
                       addOns={addOns}
                       seatsIOSelectedObjects={seatsIOSelectedObjects}
                       confirm={confirm}
                       ticketInsurance={ticketInsurance}
                       selectedMembershipProduct={selectedMembershipProduct}
                       resaleListings={resaleListings}
                       resaleOrderItems={resaleOrderItems} />
        </div>
      </div>
      <div className="row"
           style={{"paddingTop": "5px"}}>
        <div className="col d-flex align-items-center">
          <p className="mb-0"
             style={{"fontSize": "14px", "color": "var(--widget-secondary-text-color, #686868)"}}>
            Powered by
            <PoweredByLogo />
          </p>
        </div>
        <div
          className="col text-right"
          style={{
            alignItems: "center",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {!isPlacingOrder ? (
            <a href="#"
               style={{"marginRight": "30px"}}
               onClick={
                 (e) => {
                   e.preventDefault();
                   postToParent("opendateOrderCanceled", {});
                   deleteTicketReservation(csrfToken, confirm, ticketReservation);
                 }
               }>
              Cancel
            </a>
          ) : null}
          <Tippy content="Checkout agreements must be accepted"
                 disabled={requiredAgreementsAccepted}>
            <div>
              {confirm.checkout_captcha_required ? (
                <TurnstileChallenge
                  ref={turnstileRef}
                  sitekey={gon.turnstile_site_key}
                />
              ) : null}
              <button type="submit"
                      aria-busy={isPlacingOrder || verifyingHuman}
                      className={`btn btn-danger ${!canCheckout(ticketReservation, isPlacingOrder || verifyingHuman, maxTicketsPerEmailReached) ? "disabled" : ""}`}>
                {(isPlacingOrder || verifyingHuman) ? (
                  <React.Fragment>
                    <img src="/uploading-loading.gif"
                        className="mr-1"
                        style={{width: "16px"}} />
                    <strong>Placing Order...</strong>
                  </React.Fragment>
                ) : (
                  <strong>Place Order</strong>
                )}
              </button>
            </div>
          </Tippy>
        </div>
      </div>
      {confirm.checkout_captcha_required ? (
        <div className='row mt-3'>
          <div className='col-12 text-right'>
            <p className='mb-0 text-muted' style={{"fontSize": "10px"}}>
              This site is protected by Cloudflare Turnstile and the Cloudflare <a target="_blank" rel="noopener noreferrer" href="https://www.cloudflare.com/privacypolicy/">Privacy Policy</a> and <a target="_blank" rel="noopener noreferrer" href="https://www.cloudflare.com/website-terms/">Terms of Service</a> apply.
            </p>
          </div>
        </div>
      ) : null}
    </form>
    </div>
  );
};

CheckoutForm.propTypes = {
  team: PropTypes.object.isRequired,
  useDefaultTheme: PropTypes.bool,
  widgetTemplateId: PropTypes.number,
  widgetType: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketReservation: PropTypes.object,
  deleteTicketReservation: PropTypes.func.isRequired,
  stripePromise: PropTypes.object,
  ticketReservationChanged: PropTypes.func.isRequired,
  stripeError: PropTypes.object,
  stripeErrorChanged: PropTypes.func.isRequired,
  updateTicketReservation: PropTypes.func.isRequired,
  rootUrl: PropTypes.string.isRequired,
  stripePaymentRequired: PropTypes.func.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  isPlacingOrder: PropTypes.bool,
  isPlacingOrderChanged: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  customFieldValues: PropTypes.array,
  customFieldValueChanged: PropTypes.func.isRequired,
  postToParent: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  autoSaveTicketReservation: PropTypes.func.isRequired,
  userContext: PropTypes.object,
  saveGeoCodedStateCode: PropTypes.func.isRequired,
  verticalInsureClientID: PropTypes.string,
  registerTicketInsurance: PropTypes.func.isRequired,
  insuranceOfferState: PropTypes.string,
  insuranceOfferStateChanged: PropTypes.func.isRequired,
  ticketInsurance: PropTypes.object,
  deleteTicketInsurance: PropTypes.func.isRequired,
  hasInsuranceQuote: PropTypes.bool,
  hasInsuranceQuoteChanged: PropTypes.func.isRequired,
  defaultCountry: PropTypes.string,
  selectedMembershipProduct: PropTypes.object,
  resaleListings: PropTypes.array,
  resaleOrderItems: PropTypes.array
};

export default CheckoutForm;
